/**
 * Authorization utilities for accounting module
 *
 * Provides role-based access control for sensitive accounting operations.
 * Uses RBAC roles with temporary username fallback until backend RBAC is deployed.
 */

/**
 * Users authorized to create/edit Journal Entries
 * Temporary fallback until RBAC deployed
 */
const JOURNAL_ENTRY_CREATORS = [
  "brandi.clark",
  "carrie",
  "clay",
] as const;

/**
 * Users authorized to manage fiscal periods (close/reopen months)
 * Temporary fallback until RBAC deployed
 */
const PERIOD_MANAGERS = ["brandi.clark", "carrie", "clay"] as const;

/**
 * Users authorized to make CPA adjustments to prior year
 * Temporary fallback until RBAC deployed
 */
const CPA_ADJUSTMENT_USERS = ["brandi.clark", "carrie", "clay"] as const;

/**
 * User interface for authorization checks
 */
export interface AuthorizableUser {
  username: string;
  role?: string;
  roles?: string[];
  email?: string;
}

/**
 * Check if a user has any of the specified roles
 */
const hasRole = (user: AuthorizableUser, roles: string[]): boolean => {
  if (user.roles?.length) {
    return user.roles.some((r) => roles.includes(r));
  }
  if (user.role) {
    return roles.includes(user.role);
  }
  return false;
};

/**
 * Check if user can create/edit Journal Entries
 */
export const canCreateJournalEntry = (user: AuthorizableUser | null): boolean => {
  if (!user) return false;
  if (hasRole(user, ["accounting_admin", "period_manager"])) return true;
  // Temporary fallback until RBAC deployed
  return JOURNAL_ENTRY_CREATORS.includes(
    user.username as (typeof JOURNAL_ENTRY_CREATORS)[number]
  );
};

/**
 * Check if user can manage fiscal periods (close/reopen)
 */
export const canManageFiscalPeriods = (user: AuthorizableUser | null): boolean => {
  if (!user) return false;
  if (hasRole(user, ["accounting_admin", "period_manager"])) return true;
  // Temporary fallback until RBAC deployed
  return PERIOD_MANAGERS.includes(
    user.username as (typeof PERIOD_MANAGERS)[number]
  );
};

/**
 * Check if user can adjust closed periods
 */
export const canAdjustClosedPeriod = (user: AuthorizableUser | null): boolean => {
  if (!user) return false;
  if (hasRole(user, ["accounting_admin", "period_manager"])) return true;
  // Temporary fallback until RBAC deployed
  return PERIOD_MANAGERS.includes(
    user.username as (typeof PERIOD_MANAGERS)[number]
  );
};

/**
 * Check if user can create CPA adjustments
 * CPA adjustments are only allowed April-September
 */
export const canCreateCPAAdjustment = (user: AuthorizableUser | null): boolean => {
  if (!user) return false;

  // Check if user is authorized (role-based first, then username fallback)
  const isAuthorized =
    hasRole(user, ["accounting_admin"]) ||
    CPA_ADJUSTMENT_USERS.includes(
      user.username as (typeof CPA_ADJUSTMENT_USERS)[number]
    );

  if (!isAuthorized) return false;

  // Check if within CPA adjustment window (April-September)
  const currentMonth = new Date().getMonth() + 1; // 1-12
  return currentMonth >= 4 && currentMonth <= 9;
};

/**
 * Check if user can void transactions
 * Requires accountant role or higher, or be in the elevated users list
 */
export const canVoidTransactions = (user: AuthorizableUser | null): boolean => {
  if (!user) return false;

  // Check role-based access first
  if (hasRole(user, ["accounting_admin", "payment_processor", "invoice_approver"])) {
    return true;
  }

  // Check if user is in elevated users list (temporary fallback)
  if (
    PERIOD_MANAGERS.includes(
      user.username as (typeof PERIOD_MANAGERS)[number]
    )
  ) {
    return true;
  }

  // Legacy single-role check
  const voidRoles = ["accountant", "admin", "administrator"];
  return user.role ? voidRoles.includes(user.role.toLowerCase()) : false;
};
